import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import SentenceView from './SentenceView';
import ParagraphView from './ParagraphView';
import WordPopup from './WordPopup';
import { useVocab } from '../hooks/useVocab';
import { useProgress } from '../hooks/useProgress';
import { useGloss } from '../hooks/useGloss';
import { useWordTimes } from '../hooks/useWordTimes';
import { stopAll, playWord } from '../utils/audio';
import { cleanWord } from '../utils/wordText';
import useOrth from '../hooks/useOrth';
import { orthForm, hasOrth } from '../orth';
import { MEDIA_BASE, MEDIA_V } from '../api';

// Same audio-URL formula as SentenceView (filename part encoded; CJK/space safe).
const encPath = (a) => { const i = a.indexOf('/'); return i < 0 ? encodeURIComponent(a) : a.slice(0, i + 1) + encodeURIComponent(a.slice(i + 1)); };
// A silent unit's clip is blank, so it has no playable audio however it is
// reached — the word popup opens without a ▶ rather than with one that does nothing.
const audioUrlOf = (item) => { const s = item && !item.silent && (item.src || item.audio); return s ? `${MEDIA_BASE}/audio/${encPath(s)}${MEDIA_V}` : null; };

// The id that names a reading position durably, per view. 句 and 片 units carry
// ids straight out of the story data (p01s01, p01s01g01) and those hold still
// across an edit. 段 units do NOT: allParagraphs builds `para_${pi}` from the
// loop counter, which is the same counting the anchor exists to stop trusting,
// so a paragraph is anchored by its FIRST SENTENCE's id instead. Returns null
// when the unit has nothing stable to name it by — the caller falls back to the
// index, which is where we were before.
const anchorOf = (view, item) =>
  (!item ? null : view === 'paragraph' ? (item.sentenceId || null) : (item.id || item.sentenceId || null));

export default function Reader({ text, target, onBack, onJumpToReview }) {
  const [viewMode, setViewMode] = useState('sentence');   // phone default
  // Spelling overlay: print the modern form of the words the book writes in the
  // 1970s liturgical orthography. Purely presentational (see src/orth.js), so it
  // is a plain localStorage pref like srs_direction rather than synced state --
  // nothing downstream of it is worth restoring onto another device.
  const [modernOrth, setModernOrth] = useState(() => localStorage.getItem('orth_modern') === '1');
  useEffect(() => { localStorage.setItem('orth_modern', modernOrth ? '1' : '0'); }, [modernOrth]);
  const orthTable  = useOrth(text.dialect, modernOrth);
  const displayWord = useCallback((w) => orthForm(orthTable, w), [orthTable]);
  const [itemIdx, setItemIdx]   = useState(0);
  const visibleSentRef = useRef(null);   // sentence at the top of the 文 viewport (for switching views in place)
  const [popup, setPopup]       = useState(null);
  const [tocOpen, setTocOpen]   = useState(false);   // 目錄 pull-down in the title bar
  const { getStatus, setStatus } = useVocab();
  const { getPosition, savePosition, ready: progressReady } = useProgress();
  const { getGloss, setGloss } = useGloss();
  const { getTimes: rawGetTimes } = useWordTimes();   // read-only: timings are baked, no setTimes
  // Namespace timing keys by story code so any pre-existing overrides resolve per story
  // (they all share sentence ids like p01s01).
  const code = text.code || text.id;
  const getTimes = useCallback((sid, wi, fb) => rawGetTimes(`${code}:${sid}`, wi, fb), [rawGetTimes, code]);

  // All segments (SRT-level clips): paragraphs → sentences → segments
  const allSegments = useMemo(() =>
    (text.paragraphs || []).flatMap(p =>
      (p.sentences || []).flatMap(s =>
        (s.segments && s.segments.length > 0)
          // A 片 inherits its sentence's silence: the 17 unread Paiwan chronology
          // lines kept their segments, and a segment that still names a clip the
          // sentence no longer has would 404 in 片 view and stall the chain.
          ? s.segments.map(g => ({ ...g, sentenceId: s.id, paragraphId: p.id,
                                   silent: !!(g.silent || s.silent), silentNote: g.silentNote || s.silentNote }))
          : [{ ...s, sentenceId: s.id, paragraphId: p.id }]  // no sub-segments: sentence IS the segment
      )
    ),
  [text.paragraphs]);

  // All sentences
  const allSentences = useMemo(() =>
    (text.paragraphs || []).flatMap((p, pi) =>
      (p.sentences || []).map(s => ({ ...s, paragraphId: p.id, paragraphIdx: pi }))
    ),
  [text.paragraphs]);

  // One item per paragraph (段 view) — a span of the source from the first
  // sentence's start to the last sentence's end, with all the paragraph's words.
  const allParagraphs = useMemo(() =>
    (text.paragraphs || []).map((p, pi) => {
      const ss = p.sentences || [];
      // A sentence whose clip is blank contributes no audio: the 段 borrows its
      // clip identity from the first sentence that can actually be heard, and the
      // player steps over the blank parts (one of them lasts 24 s).
      const heard = ss.filter(s => !s.silent);
      const first = heard[0] || ss[0] || {}, last = heard[heard.length - 1] || ss[ss.length - 1] || {};
      const trans = ss.map(s => s.translation).filter(Boolean).join('');
      const mute = ss.find(s => s.silent);
      return {
        id: `para_${pi}`, paragraphId: p.id, sentenceId: first.id,
        src: first.src, audio: first.audio, origin: first.origin ?? 0, srcDur: first.srcDur,
        start: first.start, end: last.end,
        words: ss.flatMap(s => s.words || []),
        // each sentence is its OWN clip -> the player chains these for whole-paragraph playback
        // silent parts STAY in the list: partForWord() walks it to map a flat
        // paragraph word index onto its sentence, and dropping them would shift
        // every later word onto the wrong clip.
        parts: ss.map(s => ({ sentenceId: s.id, src: s.src, audio: s.audio, origin: s.origin ?? 0, silent: !!s.silent,
                              srcDur: s.srcDur,   // rolls() needs it: without it every part got the 0.15s tail that bleeds into the next clip's first syllable
                              start: s.start, end: s.end, words: s.words || [] })),
        translation: trans || null,
        gaps: ss.flatMap(s => s.gaps || []),
        silent: heard.length === 0,
        silentNote: mute ? (heard.length ? '錄音部分從缺：本段有句子的音檔為空白，待補錄' : mute.silentNote) : null,
      };
    }),
  [text.paragraphs]);

  const allItems = viewMode === 'segment' ? allSegments
                 : viewMode === 'paragraph' ? allParagraphs
                 : allSentences;

  // Part (chapter) starts mapped to the CURRENT view's item indices — for the
  // "一 · 句13/28"-style position + jump panel in 片/句/段 (ported from
  // seediq-app). Sections are {n,title,from,to} 段-id ranges.
  const chapterStarts = useMemo(() => {
    const secs = text.sections || [];
    if (!secs.length) return null;
    const CJK = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
    const idxOf = (pid) => {
      if (viewMode === 'segment') return allSegments.findIndex(g => g.paragraphId === pid);
      if (viewMode === 'paragraph') return (text.paragraphs || []).findIndex(p => p.id === pid);
      return allSentences.findIndex(s => s.paragraphId === pid);
    };
    const starts = secs.map(sec => ({ n: CJK[sec.n] || String(sec.n), label: sec.title || '', at: idxOf(sec.from) }))
                       .filter(x => x.at >= 0);
    return starts.length ? starts : null;
  }, [text.sections, text.paragraphs, viewMode, allSegments, allSentences]);

  // Which act (目錄 entry) the current item sits in — works in every view because
  // chapterStarts.at is already mapped to the active view's item indices.
  const curActIdx = useMemo(() => {
    if (!chapterStarts) return -1;
    let ci = 0;
    for (let i = 0; i < chapterStarts.length; i++) if (chapterStarts[i].at <= itemIdx) ci = i;
    return ci;
  }, [chapterStarts, itemIdx]);

  // Jump to an act from the 目錄: land on its first unit in the current view.
  // In 文 this sets activeSentId, which pages ParagraphView to that part.
  const jumpToAct = useCallback((i) => {
    if (!chapterStarts || !chapterStarts[i]) return;
    stopAll(); setPopup(null);
    setItemIdx(chapterStarts[i].at);
    setTocOpen(false);
  }, [chapterStarts]);

  // rebuilds change unit counts between sessions — a restored index may overshoot
  useEffect(() => {
    if (allItems.length && itemIdx > allItems.length - 1) setItemIdx(allItems.length - 1);
  }, [allItems.length, itemIdx]);

  // 跳到故事 from 複習: land at the word honouring the jump's view + focus.
  //  review-origin → view 'segment', focus → 片 with the word highlighted.
  //  reader-origin → the view I started in, no highlight (back to reading); 文 scrolls
  //  the word's sentence into view (ParagraphView's activeSentId scrollIntoView).
  useEffect(() => {
    if (!target) return;
    const view = target.view || 'segment';
    setViewMode(view);
    let i = -1;
    if (view === 'segment') i = allSegments.findIndex(g => g.id === target.segId);
    else if (view === 'paragraph') i = (text.paragraphs || []).findIndex(p => (p.sentences || []).some(s => s.id === target.sentId));
    else i = allSentences.findIndex(s => s.id === target.sentId);   // sentence | story
    if (i >= 0) setItemIdx(i);
  }, [target, allSegments, allSentences, text.paragraphs]);

  // 跳到故事 → open WORD-VIEW (the popup) on the landed word, matching the phone. The
  // anchored word is marked `.wordview-focus` (in whichever view we land in: 片/句/段/文),
  // so once it's in the DOM we click it — reusing that view's own handleWordClick, which
  // builds the correct popup; the focus-effect below then autoplays it. Once per jump.
  const jumpFocusedRef = useRef(null);
  const skipNextFocusPlayRef = useRef(false);
  useEffect(() => {
    if (!target || !target.focus) { jumpFocusedRef.current = null; return; }
    if (jumpFocusedRef.current === target) return;                     // already focused this jump
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector('.wordview-focus');
      if (!el) return;                                                 // not rendered yet — retry on next settle
      jumpFocusedRef.current = target;
      // In 片/句/段 the unit-arrival autoplay plays the whole unit on landing — so DON'T also
      // word-play here (that races + clips via WORD_TRIM). In 文 there's no unit autoplay, so
      // let the word play. We just open word-view on the word either way.
      if (target.view !== 'story') skipNextFocusPlayRef.current = true;
      el.click();
      // Backstop: the flag is meant to suppress exactly the click above, and the
      // focus-effect consumes it when it runs. It does NOT run if that click
      // re-focuses the word that is ALREADY focused (same focusKey — React skips
      // the effect). Left armed, the flag would then silently eat the user's next
      // REAL word tap, in a reader session that has no way to show it happened.
      setTimeout(() => { skipNextFocusPlayRef.current = false; }, 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [target, itemIdx, viewMode, allItems]);

  // Autoplay on word-FOCUS: whenever word-view (the popup) focuses a new word — by tap,
  // by stepping ←/→, or by a 跳到故事 jump — play it, in every view incl 文. Passive
  // reading (no popup open) stays silent. Keyed on the focused word's identity so gloss/
  // timing edits don't replay it.
  const focusKey = popup ? `${popup.sentenceId}:${popup.wordIdx}` : null;
  useEffect(() => {
    // Consume the jump-suppression flag BEFORE any early return. It used to be
    // cleared only on the line below, so a jump landing on a word with no
    // audioUrl bailed out one line early and left the flag armed — and the flag
    // then swallowed the next word the reader actually tapped.
    const skipThisFocus = skipNextFocusPlayRef.current;
    skipNextFocusPlayRef.current = false;
    if (!popup || !popup.audioUrl) return;
    if (skipThisFocus) return;                     // jump-landing: unit autoplay handles it
    const t = getTimes(popup.sentenceId, popup.wordIdx, popup.word);
    playWord(popup.audioUrl, (popup.audioStart ?? 0) + t.start, (popup.audioStart ?? 0) + t.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  // Restore saved position once it's available (progress loads async from the
  // backend, so getPosition may be 0 on first render).
  const restoredRef = useRef(null);
  useEffect(() => { restoredRef.current = null; }, [text.id]);
  useEffect(() => {
    if (!progressReady || restoredRef.current === text.id) return;   // wait for /progress to load
    if (target) { restoredRef.current = text.id; return; }           // a 跳到故事 jump owns the position — don't override it with saved progress
    // Saved as "view:index:anchor" — a bare index is meaningless without the
    // view it counted (片 34 restored into 句 view lands on the wrong unit),
    // and index-plus-view is meaningless the moment the TEXT can change under
    // it. The anchor is the unit's sentence id (p01s01), stable across content
    // edits; the index survives only as the fallback for when that id is gone.
    //
    // WHY THE ANCHOR HAS TO EXIST BEFORE THE STORIES LEAVE THE BUNDLE: while a
    // story ships inside the deploy, editing one shifts every later index at a
    // moment somebody is watching it happen. Once the stories are fetched at
    // runtime, inserting one sentence silently walks every reader's bookmark
    // down the book — no error, no version change, just the wrong place, in
    // browsers nobody can reach. Written first for that reason, not last.
    // Legacy "view:index" and bare-number saves still restore as they always did.
    const saved = getPosition(text.id);
    let view = null, idx = 0, anchor = null;
    if (typeof saved === 'string' && saved.includes(':')) {
      const [v, i, a] = saved.split(':');
      view = v; idx = parseInt(i, 10) || 0; anchor = a || null;
    } else {
      idx = Number(saved) || 0;
    }
    if (anchor) {
      // Resolve against the SAVED view's list, not allItems: setViewMode below
      // has not applied yet, so allItems is still the outgoing view's list.
      const list = view === 'segment' ? allSegments
                 : view === 'paragraph' ? allParagraphs
                 : allSentences;
      const at = list.findIndex(it => anchorOf(view, it) === anchor);
      if (at >= 0) idx = at;                     // id wins; the index was only ever a guess
    }
    if (view && view !== viewMode) setViewMode(view);
    if (idx > 0) setItemIdx(Math.max(0, idx));   // clamped against the list on render
    restoredRef.current = text.id;                                   // mark restored (even if saved=0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text.id, getPosition, progressReady, target]);

  useEffect(() => {
    if (restoredRef.current !== text.id) return;                    // don't save (clobber 0) before restore
    // view-qualified AND id-anchored — see restore above. The index stays in the
    // string as the fallback for a unit whose id has since been removed.
    const a = anchorOf(viewMode, allItems[itemIdx]);
    savePosition(text.id, `${viewMode}:${itemIdx}${a ? `:${a}` : ''}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIdx, viewMode, text.id, savePosition]);

  // LingQ-style: moving on from a sentence/fragment marks its still-"new" words
  // as "known" (words you explicitly set to "learning" are left alone).
  const prevIdxRef  = useRef(itemIdx);
  const prevViewRef = useRef(viewMode);
  useEffect(() => {
    // Never auto-mark in whole-story (文) view, or when switching views.
    if (viewMode === 'story' || prevViewRef.current !== viewMode) {
      prevViewRef.current = viewMode;
      prevIdxRef.current  = itemIdx;
      return;
    }
    const prev = prevIdxRef.current;
    if (prev !== itemIdx) {
      const left = allItems[prev];
      (left?.words || []).forEach(w => {
        const core = cleanWord(w.text, text.dialect);
        if (core && getStatus(text.dialect, core) === 'new') setStatus(text.dialect, core, 'known');
      });
      prevIdxRef.current = itemIdx;
    }
  }, [itemIdx, viewMode, allItems, getStatus, setStatus, text.dialect]);

  // For popup: find the words array for whatever is currently shown
  const findItemWords = useCallback((sentenceId) => {
    // Check segments first, then sentences
    const seg = allSegments.find(g => g.id === sentenceId);
    if (seg) return seg.words || [];
    const sent = allSentences.find(s => s.id === sentenceId);
    return sent?.words || [];
  }, [allSegments, allSentences]);

  // The unit's Chinese translation — gloss fallback when the word isn't in the
  // dictionary (affixed forms often miss; the aligned 中文 line still helps).
  const findItemTranslation = useCallback((sentenceId) => {
    const seg = allSegments.find(g => g.id === sentenceId);
    if (seg && seg.translation) return seg.translation;
    const sent = allSentences.find(s => s.id === sentenceId);
    return (sent && sent.translation) || null;
  }, [allSegments, allSentences]);

  const handleWordClick = useCallback((word, wordIdx, sentenceId, audioUrl, audioStart, e) => {
    setPopup({ word, wordIdx, sentenceId, audioUrl, audioStart,
               allWords: findItemWords(sentenceId) });
  }, [findItemWords]);

  // The word currently being heard (reported by the playing view) — so a view switch lands
  // on the EXACT segment/sentence you're listening to, not the unit's first.
  const playingWordRef = useRef(null);
  const onPlayingWord = useCallback((pw) => { playingWordRef.current = pw; }, []);
  const segIdForWord = useCallback((sentId, wIdx) => {
    const s = allSentences.find((x) => x.id === sentId);
    const segs = (s && s.segments) || [];
    let acc = 0;
    for (const g of segs) { const n = (g.words || []).length; if (wIdx != null && wIdx < acc + n) return g.id; acc += n; }
    return segs[0] ? segs[0].id : null;
  }, [allSentences]);

  // Switch view level while keeping your place: anchor on the sentence (and word) you're at,
  // and map it into the target level — the exact segment for 片, the sentence, or the paragraph.
  const goToView = (mode, sentId, wIdx) => {
    stopAll(); setPopup(null); setViewMode(mode);
    let newIdx = 0;
    if (sentId != null) {
      if (mode === 'paragraph') {
        const f = (text.paragraphs || []).findIndex(p => (p.sentences || []).some(s => s.id === sentId));
        if (f >= 0) newIdx = f;
      } else if (mode === 'segment') {
        const segId = segIdForWord(sentId, wIdx);
        let found = segId ? allSegments.findIndex(g => g.id === segId) : -1;
        if (found < 0) found = allSegments.findIndex(it => (it.sentenceId || it.id) === sentId);
        if (found >= 0) newIdx = found;
      } else { // sentence | story — both index allSentences
        const found = allSentences.findIndex(it => (it.sentenceId || it.id) === sentId);
        if (found >= 0) newIdx = found;
      }
    }
    setItemIdx(newIdx);
  };

  const handleViewChange = (mode) => {
    const pw = playingWordRef.current;
    let sentId = null, wIdx = null;
    if (pw) { sentId = pw.sentId; wIdx = pw.wIdx; }                                          // playing → land where you're hearing
    else if (viewMode === 'story' && visibleSentRef.current) sentId = visibleSentRef.current; // 文 reading → the sentence at top
    else { const cur = allItems[itemIdx]; sentId = cur ? (cur.sentenceId || cur.id) : null; }
    goToView(mode, sentId, wIdx);
  };

  return (
    <div className="reader">
      <div className="reader-top">
        <div className="rd-header">
          <button className="rd-back" onClick={() => { stopAll(); onBack(); }}>‹</button>
          {chapterStarts ? (
            <button className="rd-titlebtn" onClick={() => setTocOpen(o => !o)} aria-expanded={tocOpen}>
              <span className="rd-title">{text.title}</span>
              {curActIdx >= 0 && (
                <span className="rd-actchip">{chapterStarts[curActIdx].n}・{chapterStarts[curActIdx].label}<span className="rd-caret">{tocOpen ? '▲' : '▾'}</span></span>
              )}
            </button>
          ) : (
            <h2 className="rd-title">{text.title}</h2>
          )}
          <span className="rd-right" />
        </div>
        {tocOpen && chapterStarts && (
          <>
            <div className="rd-toc-scrim" onClick={() => setTocOpen(false)} />
            <div className="rd-toc-menu">
              <div className="rd-toc-head">《{text.title}》目錄</div>
              {chapterStarts.map((c, i) => (
                <button key={i} className={`rd-toc-item${i === curActIdx ? ' cur' : ''}`} onClick={() => jumpToAct(i)}>
                  <span className="rd-toc-n">{c.n}</span><span className="rd-toc-label">{c.label}</span>
                  {i === curActIdx && <span className="rd-toc-here">目前</span>}
                </button>
              ))}
            </div>
          </>
        )}
        <div className="modes">
          <button className={`mode-btn${viewMode === 'segment'   ? ' active' : ''}`} onClick={() => handleViewChange('segment')}>片</button>
          <button className={`mode-btn${viewMode === 'sentence'  ? ' active' : ''}`} onClick={() => handleViewChange('sentence')}>句</button>
          <button className={`mode-btn${viewMode === 'paragraph' ? ' active' : ''}`} onClick={() => handleViewChange('paragraph')}>段</button>
          <button className={`mode-btn${viewMode === 'story'     ? ' active' : ''}`} onClick={() => handleViewChange('story')}>文</button>
          {hasOrth(text.dialect) && (
            <button
              className={`mode-btn orth-btn${modernOrth ? ' active' : ''}`}
              onClick={() => setModernOrth(v => !v)}
              title={modernOrth ? '正在顯示今日通行的拼寫，點一下看原文' : '本書沿用一九七〇年代教會拼寫法，點一下改看今日通行的寫法'}
            >今寫</button>
          )}
        </div>
      </div>

      <div className="reader-body" onClick={(e) => { if (e.target.closest && e.target.closest('.wave')) return; setPopup(null); }}>
        {viewMode === 'story' ? (
          <ParagraphView
            text={text}
            activeSentId={allSentences[itemIdx]?.id}
            onVisibleSentence={(sid) => { visibleSentRef.current = sid; }}
            onChapterChange={(sid) => { const i = allSentences.findIndex(s => s.id === sid); if (i >= 0) setItemIdx(i); }}
            onPlayingWord={onPlayingWord}
            onWordClick={handleWordClick}
            getWordStatus={(w) => getStatus(text.dialect, w)}
            focusSentId={target && target.focus && target.view === 'story' ? target.anchorId : null}
            focusWIdx={target && target.focus && target.view === 'story' ? target.anchorWIdx : -1}
            displayWord={displayWord}
          />
        ) : (
          <SentenceView
            code={code}
            dialect={text.dialect}
            paraMode={viewMode === 'paragraph'}
            returnWordIdx={(target && target.focus && allItems[itemIdx] && allItems[itemIdx].id === target.anchorId) ? target.anchorWIdx : -1}
            allSentences={allItems}
            sentenceIdx={itemIdx}
            onSentenceChange={setItemIdx}
            onPlayingWord={onPlayingWord}
            onWordClick={handleWordClick}
            getWordStatus={(w) => getStatus(text.dialect, w)}
            getTimes={getTimes}
            popupOpen={!!popup}
            selWordIdx={(() => {
              const u = allItems[itemIdx];
              if (!popup || !u) return -1;
              if (popup.sentenceId === u.id) return popup.wordIdx;
              // 段: popup holds the word's own sentence id + sentence-local index —
              // map back to the paragraph's flat word index for the highlight.
              if (u.parts) {
                let acc = 0;
                for (const p of u.parts) {
                  if (p.sentenceId === popup.sentenceId) return acc + popup.wordIdx;
                  acc += (p.words || []).length;
                }
              }
              return -1;
            })()}
            chapters={chapterStarts}
            unitName={viewMode === 'segment' ? '片' : viewMode === 'paragraph' ? '段' : '句'}
            displayWord={displayWord}
          />
        )}
      </div>

      {popup && (
        <div className="word-sheet-anchor">
          <WordPopup
            word={popup.word}
            dialect={text.dialect}
            sentenceId={popup.sentenceId}
            wordIdx={popup.wordIdx}
            allWords={popup.allWords}
            audioUrl={popup.audioUrl}
            audioStart={popup.audioStart}
            unitTranslation={findItemTranslation(popup.sentenceId)}
            status={getStatus(text.dialect, popup.word.text)}
            onStatusChange={(s) => {
              setStatus(text.dialect, popup.word.text, s,
                { code, itemId: popup.sentenceId, wi: popup.wordIdx });
            }}
            onClose={(jumpWord, jumpIdx) => {
              // '__cross__': stepping past a segment edge → adjacent segment (jumpIdx = ±1).
              // Advance the reader to that segment and open word-view on its first/last word.
              if (jumpWord === '__cross__') {
                const cur = allItems.findIndex(it => it.id === popup.sentenceId);
                if (cur >= 0) {
                  const ni = cur + jumpIdx;
                  if (ni < 0 || ni >= allItems.length) return;   // at the very ends — keep the popup
                  const it = allItems[ni];
                  const ws = it.words || [];
                  if (!ws.length) return;
                  const wi = jumpIdx > 0 ? 0 : ws.length - 1;
                  setItemIdx(ni);
                  setPopup({ word: { ...ws[wi], text: cleanWord(ws[wi].text, text.dialect) }, wordIdx: wi,
                    sentenceId: it.id, audioUrl: audioUrlOf(it), audioStart: it.origin ?? 0,
                    allWords: ws });
                  return;
                }
                // 段: the popup holds a sentence id INSIDE a stitched paragraph — cross to
                // the adjacent part (sentence-clip), spilling into the neighbouring paragraph.
                const ii = allItems.findIndex(it => (it.parts || []).some(p => p.sentenceId === popup.sentenceId));
                if (ii < 0) return;
                let ni = ii, npi = allItems[ii].parts.findIndex(p => p.sentenceId === popup.sentenceId) + jumpIdx;
                if (npi < 0 || npi >= allItems[ii].parts.length) {
                  ni = ii + jumpIdx;
                  if (ni < 0 || ni >= allItems.length) return;
                  const np = allItems[ni].parts || [];
                  if (!np.length) return;
                  npi = jumpIdx > 0 ? 0 : np.length - 1;
                }
                const part = allItems[ni].parts[npi];
                const ws = part.words || [];
                if (!ws.length) return;
                const wi = jumpIdx > 0 ? 0 : ws.length - 1;
                if (ni !== itemIdx) setItemIdx(ni);
                setPopup({ word: { ...ws[wi], text: cleanWord(ws[wi].text, text.dialect) }, wordIdx: wi,
                  sentenceId: part.sentenceId, audioUrl: audioUrlOf(part), audioStart: part.origin ?? 0,
                  allWords: ws });
                return;
              }
              // jumpWord must be a real word token (has timing); ignore stray events
              if (jumpWord && jumpWord.start !== undefined) {
                setPopup(prev => ({ ...prev,
                  word: { ...jumpWord, text: cleanWord(jumpWord.text, text.dialect) },
                  wordIdx: jumpIdx,
                  allWords: findItemWords(prev.sentenceId) }));
              } else {
                setPopup(null);
              }
            }}
            onJumpToReview={onJumpToReview ? () => onJumpToReview(cleanWord(popup.word.text, text.dialect), text.dialect, viewMode, popup.sentenceId, popup.wordIdx) : undefined}
            getGloss={getGloss}
            setGloss={setGloss}
            getTimes={getTimes}
          />
        </div>
      )}
    </div>
  );
}
